import {
  Button,
  FormControl,
  Select,
  Typography,
  Box,
  TextField,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useState } from "react";
import {
  Category,
  InvestmentFeedbackDto,
  RecommendationsClient,
  UserDetailDto,
} from "../../../utilities/backend/client";
import { MenuItemEllipsis } from "../../../styles/StyledComponents";

interface InvestFeedbackProps {
  user?: UserDetailDto;
  themes?: Category[];
  backClick: () => void;
  investmentInProcessShow: () => void;
}

const InvestFeedback: React.FC<InvestFeedbackProps> = ({
  user,
  themes,
  backClick,
  investmentInProcessShow,
}) => {
  const recommendationsClient = new RecommendationsClient();
  const theme = useTheme();

  const [formData, setFormData] = useState<{
    themesValue: string[];
    additionalThemes: string;
    interestedInvestmentType: string[];
    riskTolerance: number;
  }>({
    themesValue: [],
    additionalThemes: "",
    interestedInvestmentType: [],
    riskTolerance: 0,
  });

  const handleThemeChange = (event: any) => {
    setFormData({ ...formData, themesValue: event.target.value });
  };

  const handleOtherThemesChange = (event: any) => {
    setFormData({ ...formData, additionalThemes: event.target.value });
  };

  const handleInvestmentTypeChange = (event: any) => {
    setFormData({ ...formData, interestedInvestmentType: event.target.value });
  };

  const handleRiskChange = (index: number) => {
    setFormData({ ...formData, riskTolerance: index });
  };

  const handleSubmit = () => {
    if (!formData.themesValue.length) {
      return;
    }

    const tempId = localStorage.getItem("feedback-temp-email");
    const userName = localStorage.getItem("userName");

    const investmentFeedbackDto = {
      themes: formData.themesValue.map((id) => id.toString()).join(","),
      additionalThemes: formData.additionalThemes,
      interestedInvestmentType: formData.interestedInvestmentType.join(","),
      riskTolerance: formData.riskTolerance,
      ...(userName && !tempId ? { userName } : {}),
      email: user?.email ?? tempId,
    } as InvestmentFeedbackDto;

    recommendationsClient
      .recommendations_CreateFeedback(investmentFeedbackDto)
      .then(async () => {
        investmentInProcessShow();
        localStorage.removeItem("feedback-temp-email");
        localStorage.removeItem("recommendationData");
        localStorage.removeItem("campaignMinInvestment");
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        window.history.replaceState(null, "");
      });
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Button
        onClick={backClick}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "auto",
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
        }}
      >
        <ArrowBackIcon sx={{ color: "000000" }} />
        <Typography
          sx={{
            fontFamily: "Ubuntu",
            fontSize: 16,
            fontWeight: 400,
            color: "#474747",
            marginLeft: "8px",
          }}
        >
          Back
        </Typography>
      </Button>

      <Box
        sx={{
          width: "546px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontWeight: 400,
            fontSize: "32px",
            lineHeight: "48px",
            letterSpacing: "0%",
            color: "#20527F",
            paddingTop: "15px",
            [theme.breakpoints.down("sm")]: {
              paddingTop: "45px",
              width: "100%",
              fontSize: "28px",
              lineHeight: "38px",
            },
          }}
        >
          Thank you for donating to invest
        </Typography>

        <Typography
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "18px",
            letterSpacing: "0%",
            color: "#000",
            paddingTop: "15px",
            [theme.breakpoints.down("sm")]: {
              fontSize: "12px",
              lineHeight: "18px",
            },
          }}
        >
          We’d like to learn more about you, and your interests, so we can share
          new and aligned opportunities:
        </Typography>

        <Typography
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "18.38px",
            letterSpacing: "0%",
            color: "#656565",
            padding: "40px 0px 8px 0px",
          }}
        >
          Select the themes that most interest you*
        </Typography>

        <FormControl fullWidth>
          <Select
            multiple
            value={formData.themesValue}
            onChange={handleThemeChange}
            renderValue={(selected) => {
              const selectedThemes = themes
                ?.filter((theme) => selected.map(Number).includes(theme.id!))
                .map((theme) => theme.name)
                .join(", ");

              const truncatedText =
                selectedThemes?.length! > 40
                  ? `${selectedThemes?.substring(0, 40)}...`
                  : selectedThemes;

              return <Box>{truncatedText}</Box>;
            }}
            sx={{
              fontFamily: "Poppins, Sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0%",
              height: "51px",
            }}
          >
            {themes?.map((theme, index) => (
              <MenuItemEllipsis key={index} value={theme.id}>
                {theme.name}
              </MenuItemEllipsis>
            ))}
          </Select>
        </FormControl>

        <Typography
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "18.38px",
            letterSpacing: "0%",
            color: "#656565",
            padding: "24px 0px 8px 0px",
          }}
        >
          Other themes you care about
        </Typography>

        <FormControl fullWidth>
          <TextField
            value={formData.additionalThemes}
            onChange={handleOtherThemesChange}
            sx={{
              fontFamily: "Poppins, Sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0%",
              height: "51px",
              width: "100%",
            }}
          />
        </FormControl>

        <Typography
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "18.38px",
            letterSpacing: "0%",
            color: "#656565",
            padding: "28px 0px 8px 0px",
          }}
        >
          Which types of investments most interest you?
        </Typography>

        <FormControl fullWidth>
          <Select
            multiple
            value={formData.interestedInvestmentType ?? ""}
            onChange={handleInvestmentTypeChange}
            sx={{
              fontFamily: "Poppins, Sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0%",
              height: "51px",
            }}
          >
            <MenuItemEllipsis value={0}>Equity fund</MenuItemEllipsis>
            <MenuItemEllipsis value={1}>Loan fund</MenuItemEllipsis>
            <MenuItemEllipsis value={2}>Direct equity</MenuItemEllipsis>
            <MenuItemEllipsis value={3}>Direct loan</MenuItemEllipsis>
          </Select>
        </FormControl>

        <Typography
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "18.38px",
            letterSpacing: "0%",
            color: "#656565",
            paddingTop: "24px",
          }}
        >
          How do you define your risk tolerance?
        </Typography>
        <Box
          sx={{
            width: "93%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins, Sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "18.38px",
              letterSpacing: "0%",
              color: "#656565",
            }}
          >
            Low
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: "15px",
              marginTop: "20px",
              [theme.breakpoints.down("sm")]: {
                gap: "5px",
              },
            }}
          >
            {[...Array(10)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  onClick={() => handleRiskChange(index + 1)}
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor:
                      index + 1 === formData.riskTolerance
                        ? "#20527f"
                        : "#D9D9D9",
                    cursor: "pointer",
                    border:
                      index + 1 === formData.riskTolerance
                        ? "2px solid black"
                        : "none",
                    [theme.breakpoints.down("sm")]: {
                      width: "20px",
                      height: "20px",
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    [theme.breakpoints.down("sm")]: {
                      fontSize: "10px",
                    },
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            sx={{
              fontFamily: "Poppins, Sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "18.38px",
              letterSpacing: "0%",
              color: "#656565",
            }}
          >
            High
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.themesValue.length}
          sx={{
            fontFamily: "Ubuntu",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "19.2px",
            letterSpacing: "0%",
            marginTop: "50px",
            width: "100%",
            padding: "10px",
            backgroundColor: "#80B73F",
            borderRadius: "0",
            color: "#FFF",
            transition: "background-color 0.3s ease",
            "&:hover": {
              backgroundColor: "#9DCF5F",
            },
            textTransform: "none",
          }}
        >
          Submit
        </Button>
      </Box>
    </div>
  );
};

export default InvestFeedback;
